import { defineStore } from 'pinia';
import axios from 'axios';
import {
  ICitiesList,
  ICitiesMenu,
  ICitiesMessages,
  ICitiesRoutes,
  ICitiesUserCity,
  ICitiesWarehouses,
  ICitiesStore,
} from '@/storePinia/stores/cities/ICitiesStore';

interface ICities {
  userCity: ICitiesUserCity;
  routes: ICitiesRoutes;
  messages: ICitiesMessages;
  citiesMenu: ICitiesMenu;
  cities: ICitiesList;
  warehouses: ICitiesWarehouses;
}

type ICitiesStoreGetters = {
  userCityId(state: ICitiesStore): string | undefined;
};

interface ICitiesStoreActions {
  setCity(city: string): Promise<void>;
  getCities(city: string): Promise<void>;
  getCitiesWithShops(city: string): Promise<void>;
  getCitiesById(id: number): Promise<ICitiesList | undefined>;
  getWarehouses(params: {
    UF_XML_ID: string;
    warehouse: string;
    warehouseType: string;
  }): Promise<void>;
  getUserCityFromNP(): Promise<void>;
}

let abortController: null | AbortController = null;

export const useCitiesStore = defineStore<
  'cities',
  ICitiesStore,
  ICitiesStoreGetters,
  ICitiesStoreActions
>('cities', {
  state: (): ICitiesStore => ({
    userCity: {},
    routes: {},
    messages: {},
    citiesMenu: {},
    cities: [],
    warehouses: [],
  }),
  getters: {
    userCityId: (state: ICitiesStore): string | undefined => state.userCity?.ID,
  },
  actions: {
    async setCity(city: string) {
      try {
        if (this.routes?.setCity) {
          const { path, action, LANGUAGE_ID } = this.routes.setCity;
          const requestParams = { action, city, LANGUAGE_ID };

          const { status, data: responseData } = await axios.post(path, requestParams);
          if (status !== 200) {
            throw new Error(`set default city bad response status ${status}`);
          }

          this.$patch(responseData as Partial<ICities>);
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getCities(city: string) {
      try {
        if (abortController) {
          abortController.abort();
          abortController = null;
        }
        abortController = new AbortController();

        if (this.routes?.getCities) {
          const { path, action, LANGUAGE_ID } = this.routes.getCities;
          const requestParams = {
            action,
            city,
            LANGUAGE_ID,
          };

          const {
            status,
            data: responseData,
          }: {
            status: number;
            data: { cities: ICitiesList };
          } = await axios.post(path, requestParams, {
            signal: abortController.signal,
          });
          if (status !== 200) {
            throw new Error(`set default city bad response status ${status}`);
          }

          this.$patch({
            cities: responseData.cities,
          });
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getCitiesWithShops(city: string) {
      try {
        if (this.routes?.getCities) {
          const { path, action, LANGUAGE_ID } = this.routes.getCities;
          const requestParams = {
            findOnlyForStores: true,
            action,
            city,
            LANGUAGE_ID,
          };

          const {
            status,
            data: responseData,
          }: {
            status: number;
            data: { cities: ICitiesList };
          } = await axios.post(path, requestParams);
          if (status !== 200) {
            throw new Error(`set default city bad response status ${status}`);
          }
          this.$patch({
            cities: responseData.cities,
          });
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getCitiesById(id: number): Promise<ICitiesList | undefined> {
      try {
        if (this.routes?.getCitiesById) {
          const { path, action, LANGUAGE_ID } = this.routes.getCitiesById;
          const requestParams = {
            action,
            id,
            LANGUAGE_ID,
          };

          const {
            status,
            data: responseData,
          }: {
            status: number;
            data: { cities: ICitiesList };
          } = await axios.post(path, requestParams);

          if (status !== 200) {
            throw new Error(`set default city bad response status ${status}`);
          }

          return responseData.cities;
        }
      } catch (err) {
        console.error(err);
      }
      return undefined;
    },
    async getWarehouses({
      UF_XML_ID,
      warehouse,
      warehouseType,
    }: {
      UF_XML_ID: string;
      warehouse: string;
      warehouseType: string;
    }) {
      try {
        if (this.routes?.getWarehouses) {
          const { action, path, LANGUAGE_ID } = this.routes.getWarehouses;

          const {
            data,
            status,
          }: {
            status: number;
            data: { warehouses: ICitiesWarehouses };
          } = await axios.get(path, {
            params: {
              action,
              city: UF_XML_ID,
              warehouse,
              warehouseType,
              LANGUAGE_ID,
            },
          });

          if (status !== 200) {
            throw new Error(`get warehouses bad response status ${status}`);
          }

          if (data.warehouses) {
            this.$patch(data);
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    async getUserCityFromNP() {
      try {
        const {
          data,
          status,
        }: {
          data: ICities;
          status: number;
        } = await axios.post('/ajax/', {
          action: 'getUserCityFromNP',
        });

        if (status !== 200) {
          throw new Error(`get user city from NP bad response status ${status}`);
        }

        this.$state = data;
      } catch (err) {
        console.error(err);
      }
    },
  },
});
